/**
 * paymentMethodsConfig.ts — Persistencia y normalización de etiquetas de
 * métodos de pago configurables por el negocio (global por businessId).
 *
 * Filosofía de datos:
 *  - Cada método tiene un ID INTERNO FIJO E INMUTABLE (ej: "efectivo", "tarjeta",
 *    "digital_1", "digital_2", "digital_3", "digital_4").
 *  - El campo "label" es solo una ETIQUETA EDITABLE para mostrar al cajero y
 *    cliente. Renombrar "Nequi" a "Bizum" NO cambia el ID interno.
 *  - "efectivo" es universal y obligatorio; "tarjeta" es un canal opcional.
 *  - Los slots digitales pueden activarse/desactivarse. Al desactivar, NO se
 *    borra el ID: las ventas históricas conservan referencia.
 *  - Si un ID queda fuera del config (o cambia su etiqueta), las ventas
 *    históricas pueden mostrar el label snapshot que llevan en la venta
 *    (`metodoPagoLabel`, `breakdown[].methodLabel`) y como último recurso el
 *    label por defecto.
 *
 * QUÉ HACE: lee/escribe datos locales transaccionales por businessId, normaliza datos
 * y emite el evento `altenot:payment-methods-config` para que la vista de
 * cobro se actualice en caliente.
 *
 * QUÉ NO DEBE HACER: no calcula montos ni decide políticas; solo persistencia.
 */

import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";
import { getActiveCountryConfig } from "@/config/countries";

/** ID interno fijo de un método de pago. Nunca debe cambiar tras crear ventas. */
export type PaymentMethodId =
  | "efectivo"
  | "tarjeta"
  | "digital_1"
  | "digital_2"
  | "digital_3"
  | "digital_4"
  /** Legacy: ventas viejas guardadas antes de la migración a digital_*. */
  | "nequi"
  | "daviplata"
  | "transferencia"
  // Permite IDs futuros sin perder autocompletado en los literales conocidos.
  | (string & Record<never, never>);

/**
 * Configuración y utilidades de métodos de pago del negocio.
 *
 * Los TIPOS del contrato (`PaymentMethodSetting`, `PaymentMethodUsageFee`,
 * `UsageFeePaidBy`) viven en `@/types` porque caja y tienda comparten el mismo
 * contrato; aquí se importan y re-exportan por compatibilidad.
 */
import type { PaymentMethodSetting, PaymentMethodUsageFee } from "@/types";

export type {
  PaymentMethodSetting,
  PaymentMethodUsageFee,
  UsageFeePaidBy,
} from "@/types";

/** Bundle completo de la configuración de métodos de pago de un negocio. */
export interface PaymentMethodsConfig {
  /** Lista ordenada de los métodos del negocio. */
  methods: PaymentMethodSetting[];
  /** Cuándo se guardó por última vez (epoch ms). Para auditoría/UX. */
  updatedAt: number;
}

/** Etiquetas por defecto que se usan al crear el config inicial (Colombia). */
const DEFAULT_LABELS_BY_ID: Record<string, string> = {
  efectivo: "Efectivo",
  tarjeta: "Tarjeta",
  digital_1: "Nequi",
  digital_2: "Daviplata",
  digital_3: "Transferencia",
  digital_4: "Otro digital",
  digital_5: "Digital 5",
  digital_6: "Digital 6",
  // Legacy: ventas guardadas antes de la migración pueden traer estos IDs.
  nequi: "Nequi",
  daviplata: "Daviplata",
  transferencia: "Transferencia",
};

export const DEFAULT_PAYMENT_METHODS: PaymentMethodSetting[] = [
  {
    id: "efectivo",
    label: "Efectivo",
    enabled: true,
    kind: "efectivo",
    locked: true,
  },
  {
    id: "tarjeta",
    label: "Tarjeta",
    enabled: true,
    kind: "tarjeta",
    locked: false,
  },
  { id: "digital_1", label: "Nequi", enabled: true, kind: "digital" },
  { id: "digital_2", label: "Daviplata", enabled: true, kind: "digital" },
  {
    id: "digital_3",
    label: "Transferencia",
    enabled: false,
    kind: "digital",
  },
  { id: "digital_4", label: "Otro digital", enabled: false, kind: "digital" },
  { id: "digital_5", label: "Digital 5", enabled: false, kind: "digital" },
  { id: "digital_6", label: "Digital 6", enabled: false, kind: "digital" },
];

/**
 * Crea los métodos iniciales para el país activo. Solo se usa cuando todavía
 * no existe una configuración guardada, por lo que nunca renombra históricos.
 */
function getInitialPaymentMethods(): PaymentMethodSetting[] {
  const suggestedMethods = getActiveCountryConfig().defaultDigitalMethods;
  const labelsBySlot = new Map(
    suggestedMethods.map((method) => [method.slotId, method.name]),
  );

  return DEFAULT_PAYMENT_METHODS.map((method) => ({
    ...method,
    label:
      labelsBySlot.get(method.id as "digital_1" | "digital_2") ?? method.label,
  }));
}

export const DEFAULT_PAYMENT_METHODS_CONFIG: PaymentMethodsConfig = {
  methods: DEFAULT_PAYMENT_METHODS,
  updatedAt: 0,
};

/** Slots digitales reservados. Mantener este orden = orden visual. */
const DIGITAL_SLOTS = [
  "digital_1",
  "digital_2",
  "digital_3",
  "digital_4",
  "digital_5",
  "digital_6",
];

function storageKeyForBusiness(businessId: string | undefined | null): string {
  const id = (businessId || "default").trim() || "default";
  return `altenot_payment_methods_${id}`;
}

function sanitizeLabel(raw: unknown, fallback: string): string {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return fallback;
  // Cap a 24 chars para que entre en chips/UI sin romper layout.
  return s.length > 24 ? s.slice(0, 24) : s;
}

function sanitizeUsageFee(
  raw: unknown,
  kind: PaymentMethodSetting["kind"],
): PaymentMethodUsageFee | undefined {
  if (kind === "efectivo") return undefined;
  if (!raw || typeof raw !== "object") return undefined;
  const pctRaw = (raw as PaymentMethodUsageFee).pct;
  const pct =
    typeof pctRaw === "number" && Number.isFinite(pctRaw)
      ? Math.min(100, Math.max(0, Math.round(pctRaw * 100) / 100))
      : 0;
  if (pct <= 0) return undefined;
  const paidBy =
    (raw as PaymentMethodUsageFee).paidBy === "cliente" ? "cliente" : "negocio";
  return { pct, paidBy };
}

/** Incentivo (descuento al cliente) por pagar con el método: 0–100, 2 decimales. */
function sanitizeIncentivePct(raw: unknown): number | undefined {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.min(100, Math.round(n * 100) / 100);
}

function withUsageFee(
  base: PaymentMethodSetting,
  found?: Partial<PaymentMethodSetting>,
): PaymentMethodSetting {
  const usageFee = sanitizeUsageFee(found?.usageFee, base.kind);
  const incentivePct = sanitizeIncentivePct(found?.incentivePct);
  return {
    ...base,
    usageFee: usageFee ? { ...usageFee } : undefined,
    incentivePct,
  };
}

/**
 * Devuelve un config válido a partir de datos potencialmente parciales o
 * malformados. Aplica defaults conservadores y respeta los métodos `locked`.
 */
export function normalizePaymentMethodsConfig(
  raw: Partial<PaymentMethodsConfig> | null | undefined,
): PaymentMethodsConfig {
  const candidateMethods = raw?.methods;
  const incoming = Array.isArray(candidateMethods) ? candidateMethods : [];
  const initialMethods = getInitialPaymentMethods();
  const byId = new Map<string, PaymentMethodSetting>();

  for (const m of incoming) {
    if (!m || typeof m.id !== "string") continue;
    const id = m.id.trim();
    if (!id) continue;
    byId.set(id, m);
  }

  const result: PaymentMethodSetting[] = [];

  // 1) Asegurar efectivo + tarjeta primero. Solo efectivo es obligatorio.
  for (const fixed of [initialMethods[0], initialMethods[1]]) {
    const found = byId.get(fixed.id);
    result.push(
      withUsageFee(
        {
          ...fixed,
          label: sanitizeLabel(found?.label, fixed.label),
          enabled:
            fixed.id === "efectivo"
              ? true
              : typeof found?.enabled === "boolean"
                ? found.enabled
                : fixed.enabled,
          locked: fixed.id === "efectivo",
        },
        found,
      ),
    );
    byId.delete(fixed.id);
  }

  // 2) Slots digitales en orden fijo.
  for (const slotId of DIGITAL_SLOTS) {
    const found = byId.get(slotId);
    const fallback = initialMethods.find((m) => m.id === slotId);
    if (!fallback) continue;
    result.push(
      withUsageFee(
        {
          id: slotId,
          kind: "digital",
          label: sanitizeLabel(found?.label, fallback.label),
          enabled:
            found && typeof found.enabled === "boolean"
              ? found.enabled
              : fallback.enabled,
          locked: false,
        },
        found,
      ),
    );
    byId.delete(slotId);
  }

  // 3) IDs legacy que ya no están en la nueva lista pero pueden aparecer en
  //    ventas antiguas (nequi/daviplata/transferencia). NO se renderizan en
  //    el POS, pero se conservan en el config para que el resolver los
  //    encuentre. Los marcamos `enabled=false` (no aparecen como botón).
  for (const [id, m] of byId.entries()) {
    if (!id) continue;
    result.push({
      id,
      kind: "digital",
      label: sanitizeLabel(m.label, DEFAULT_LABELS_BY_ID[id] ?? id),
      enabled: false,
      locked: false,
    });
  }

  return {
    methods: result,
    updatedAt:
      typeof raw?.updatedAt === "number" && Number.isFinite(raw.updatedAt)
        ? raw.updatedAt
        : 0,
  };
}

export function loadPaymentMethodsConfig(
  businessId: string | undefined | null,
): PaymentMethodsConfig {
  try {
    return normalizePaymentMethodsConfig(
      txRead<Partial<PaymentMethodsConfig> | null>(
        storageKeyForBusiness(businessId),
        null,
      ),
    );
  } catch {
    return normalizePaymentMethodsConfig(null);
  }
}

export function persistPaymentMethodsConfig(
  businessId: string | undefined | null,
  cfg: PaymentMethodsConfig,
): PaymentMethodsConfig {
  const clean = normalizePaymentMethodsConfig({
    ...cfg,
    updatedAt: Date.now(),
  });
  try {
    runTransaction(() => {
      txWrite(storageKeyForBusiness(businessId), clean);
    });
    window.dispatchEvent(new CustomEvent("altenot:payment-methods-config"));
  } catch {
    /* SSR / entornos sin localStorage */
  }
  return clean;
}

/** Convenience: devuelve solo los métodos habilitados (para el POS). */
export function getEnabledPaymentMethods(
  cfg: PaymentMethodsConfig,
): PaymentMethodSetting[] {
  return cfg.methods.filter((m) => m.enabled);
}

/** Lookup rápido para resolver una etiqueta a partir del id en este negocio. */
export function getPaymentMethodById(
  cfg: PaymentMethodsConfig,
  id: string,
): PaymentMethodSetting | undefined {
  return cfg.methods.find((m) => m.id === id);
}

/** Etiqueta por defecto del catálogo (sin depender del config del negocio). */
export function getDefaultPaymentMethodLabel(id: string): string {
  return DEFAULT_LABELS_BY_ID[id] ?? id;
}

/**
 * % de incentivo (descuento al cliente) configurado para un método de pago.
 * Devuelve 0 si el método no tiene incentivo o el config no lo define.
 */
export function getIncentivePctForMethod(
  methodId: string,
  cfg?: PaymentMethodsConfig | null,
): number {
  if (!methodId) return 0;
  const setting = cfg ? getPaymentMethodById(cfg, methodId) : undefined;
  const pct = setting?.incentivePct;
  if (typeof pct !== "number" || !Number.isFinite(pct) || pct <= 0) return 0;
  return Math.min(100, Math.round(pct * 100) / 100);
}
